import { useEffect, useRef, useState } from "react";
import { publicApi } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";

const sid = () => {
  let s = localStorage.getItem("sasha_chat_sid");
  if (!s) {
    s = `ys-${crypto.randomUUID()}`;
    localStorage.setItem("sasha_chat_sid", s);
  }
  return s;
};

export const SashaChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hello, I'm Sasha — your host for this page. Curious about getting a Sasha of your own, the levels, or the mission? Ask me anything.",
    },
  ]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || thinking) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setThinking(true);
    try {
      const r = await publicApi.post("/sasha-chat", { session_id: sid(), message: text });
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply }]);
    } catch (_e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "I'm having a moment — please try again, or email Concierge@JanteTravelConcierges.com." },
      ]);
    } finally {
      setThinking(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          data-testid="sasha-chat-open"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-[#C03028] text-[#FAF9F6] shadow-lg px-5 py-3.5 inline-flex items-center gap-2 text-sm tracking-wide hover:bg-[#9F2620] transition-colors"
        >
          <MessageCircle size={16} /> Chat with Sasha
        </button>
      )}
      {open && (
        <div
          data-testid="sasha-chat-panel"
          className="fixed bottom-6 right-6 z-50 w-[360px] max-w-[calc(100vw-2rem)] bg-[#FAF9F6] border border-[#E3DED5] shadow-2xl flex flex-col"
          style={{ height: "480px" }}
        >
          <div className="bg-[#5B1F1B] text-[#FAF9F6] px-4 py-3 flex items-center justify-between">
            <div>
              <div className="font-serif-display text-lg leading-tight">Sasha</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#B0895C]">Your host on this page</div>
            </div>
            <button type="button" data-testid="sasha-chat-close" onClick={() => setOpen(false)} className="hover:text-[#B0895C]">
              <X size={16} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-msg-${m.role}`}
                className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "ml-auto bg-[#C03028] text-[#FAF9F6]"
                    : "bg-white border border-[#E3DED5] text-[#2A1F1A]"
                }`}
              >
                {m.content}
              </div>
            ))}
            {thinking && (
              <div className="bg-white border border-[#E3DED5] text-[#6E6E70] max-w-[85%] px-3.5 py-2.5 text-sm inline-flex items-center gap-2">
                <Loader2 size={13} className="animate-spin" /> Sasha is typing…
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          <form onSubmit={send} className="border-t border-[#E3DED5] p-3 flex gap-2 bg-white">
            <Input
              data-testid="sasha-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Sasha anything…"
              className="editorial-input h-10 text-sm flex-1"
            />
            <button
              type="submit"
              disabled={thinking || !input.trim()}
              data-testid="sasha-chat-send"
              className="btn-primary px-4 inline-flex items-center justify-center"
            >
              <Send size={15} />
            </button>
          </form>
        </div>
      )}
    </>
  );
};
